// src/components/Header.js
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getAuth, signOut } from 'firebase/auth';
import LanguageSwitcher from './LanguageSwitcher';
import { useLanguage } from '../contexts/LanguageContext';
import './HeaderButtons.css';

const Header = () => {
  const { t, language, toggleLanguage } = useLanguage();
  const navigate = useNavigate();
  const auth = getAuth();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showTitle, setShowTitle] = useState(false);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setIsLoggedIn(!!user);
    });

    return () => unsubscribe();
  }, [auth]);

  useEffect(() => {
    const timer = setTimeout(() => setShowTitle(true), 1200);
    return () => clearTimeout(timer);
  }, []);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <header style={{
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center',
      padding: '20px 40px', 
      background: 'rgba(255,255,255,0.95)',
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      borderBottom: '1px solid rgba(0,0,0,0.05)',
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      zIndex: 100,
      fontFamily: 'Poppins, Segoe UI, Arial, sans-serif',
      clipPath: 'polygon(0 0, 100% 0, 100% 90%, 95% 100%, 5% 100%, 0 90%)',
    }}>
      {/* Logo/Title Section */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '15px'
      }}>
        <div style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          gap: '18px',
          width: 'auto',
          height: '100px',
          background: 'none',
          boxShadow: 'none',
          transition: 'all 0.3s ease',
        }}>
          {/* SVG سيارة متحركة */}
          <div className="animate-car">
            <svg width="120" height="70" viewBox="0 0 120 70" fill="none" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="vanBodyReal" x1="0" y1="0" x2="120" y2="70" gradientUnits="userSpaceOnUse">
                  <stop stopColor="#1a2a6c" />
                  <stop offset="0.5" stopColor="#b21f1f" />
                  <stop offset="1" stopColor="#fdbb2d" />
                </linearGradient>
              </defs>
              {/* جسم السيارة */}
              <rect x="10" y="25" width="80" height="28" rx="8" fill="url(#vanBodyReal)" stroke="#222" strokeWidth="2" />
              {/* الكابينة */}
              <rect x="70" y="18" width="32" height="35" rx="8" fill="#e3e9f7" stroke="#222" strokeWidth="2" />
              {/* النافذة الأمامية */}
              <rect x="90" y="22" width="10" height="12" rx="2" fill="#fff" stroke="#bbb" strokeWidth="1" />
              {/* المصابيح */}
              <ellipse cx="102" cy="50" rx="3" ry="2" fill="#ffe066" stroke="#bba600" strokeWidth="1" />
              <ellipse cx="18" cy="50" rx="3" ry="2" fill="#ffe066" stroke="#bba600" strokeWidth="1" />
              {/* العجلات */}
              <ellipse cx="28" cy="60" rx="7" ry="7" fill="#222" stroke="#fff" strokeWidth="3" />
              <ellipse cx="85" cy="60" rx="7" ry="7" fill="#222" stroke="#fff" strokeWidth="3" />
              {/* خطوط ظل */}
              <rect x="15" y="40" width="15" height="2" rx="1" fill="#fff" opacity="0.2" />
              <rect x="35" y="40" width="15" height="2" rx="1" fill="#fff" opacity="0.2" />
              {/* الباب */}
              <rect x="50" y="32" width="18" height="15" rx="2" fill="#fff2" stroke="#fff" strokeWidth="0.5" />
            </svg>
          </div>
          {/* النص يظهر بعد حركة السيارة */}
          <span className={showTitle ? 'animate-title' : ''} style={{
            fontFamily: language === 'ar' ? "'Tajawal', Cairo, 'Noto Kufi Arabic', Arial, sans-serif" : 'Poppins, sans-serif',
            fontWeight: 900,
            fontSize: language === 'ar' ? '2.2rem' : '2.3rem',
            letterSpacing: language === 'ar' ? '-1px' : '-2px',
            color: '#1a2a6c',
            textShadow: '0 2px 8px #fff8, 0 1px 0 #b21f1f',
            display: 'inline-block',
            lineHeight: 1.1,
            marginTop: language === 'ar' ? '10px' : '0',
            direction: language === 'ar' ? 'rtl' : 'ltr',
            background: 'linear-gradient(90deg, #1a2a6c, #b21f1f, #fdbb2d)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            opacity: showTitle ? 1 : 0,
          }}>
            {language === 'ar' ? 'اي تي ديليفري' : 'AT Delivery'}
          </span>
        </div>
      </div>

      {/* Navigation and Actions Section */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '25px' 
      }}>
        {/* Language Switcher */}
        <div style={{
          position: 'relative',
          width: 50,
          height: 50,
          borderRadius: '50%',
          background: 'linear-gradient(45deg, #1a2a6c, #b21f1f, #fdbb2d)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 4px 15px rgba(26,42,108,0.2)',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          overflow: 'hidden',
          ':hover': {
            transform: 'scale(1.05) rotate(5deg)',
            boxShadow: '0 6px 20px rgba(26,42,108,0.3)'
          }
        }} onClick={toggleLanguage}>
          <div style={{
            position: 'relative',
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(255,255,255,0.1)',
            backdropFilter: 'blur(5px)',
          }}>
            <svg 
              width="24" 
              height="24" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="white" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
              style={{
                transition: 'all 0.3s ease',
                transform: language === 'ar' ? 'scaleX(-1)' : 'none'
              }}
            >
              <circle cx="12" cy="12" r="10" />
              <path d="M2 12h20" />
              <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
            </svg>
            <span style={{
              position: 'absolute',
              bottom: '4px',
              fontSize: '10px',
              fontWeight: 'bold',
              color: 'white',
              textTransform: 'uppercase',
              letterSpacing: '1px'
            }}>
              {language === 'ar' ? 'EN' : 'عربي'}
            </span>
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '15px' }}>
          {isLoggedIn ? (
            <button
              onClick={handleLogout}
              style={{
                padding: '12px 28px',
                fontSize: '1rem',
                fontWeight: 600,
                color: 'white',
                background: 'linear-gradient(45deg, #b21f1f, #1a2a6c)',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 15px rgba(178,31,31,0.2)',
                fontFamily: 'inherit',
                ':hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 20px rgba(178,31,31,0.3)',
                  filter: 'brightness(1.1)'
                }
              }}
            >
              {t('logout')}
            </button>
          ) : (
            <>
              <Link to="/login" style={{ textDecoration: 'none' }}>
                <button className="header-btn-login">
                  {t('login')}
                </button>
              </Link>
              
              <Link to="/signup" style={{ textDecoration: 'none' }}>
                <button className="header-btn-signup">
                  {t('signup')}
                </button>
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;